import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Upload, Send, Trash2, DollarSign, Image as ImageIcon, 
  X, CheckCircle, AlertCircle, Loader2, FolderUp, Info, CheckSquare, Square, XSquare,
  Edit2, Save, Star, Download, Play, Clock, RefreshCw, Tag
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Progress } from "../../components/ui/progress";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import TheaterMode from "../../components/TheaterMode";
import GalleryDownloadPricing from "../../components/GalleryDownloadPricing";
import OptimizedImage from "../../components/OptimizedImage";

// Event types from photographer onboarding
const EVENT_TYPES = [
  "Sports", "Wedding", "Portrait", "Event", "Commercial", 
  "Real Estate", "Product", "Fashion", "Newborn", "Family",
  "Landscape", "Concert", "Food", "Architecture", "Pet", "Other"
];

const GalleryDetailPage = () => {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  
  const [gallery, setGallery] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Bulk upload state
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePrices, setFilePrices] = useState({});
  const [uploadLimits, setUploadLimits] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Pricing mode state
  const [pricingMode, setPricingMode] = useState("same");
  const [bulkPrice, setBulkPrice] = useState("");
  
  // Selection state for bulk delete
  const [selectMode, setSelectMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Single file price dialog (legacy)
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadPrice, setUploadPrice] = useState("");
  
  // Theater mode state
  const [theaterOpen, setTheaterOpen] = useState(false);
  const [theaterIndex, setTheaterIndex] = useState(0);
  
  // Edit gallery state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    title: "",
    description: "",
    customer_name: "",
    customer_email: "",
    event_date: "",
    is_public: false,
    category: "",
    tags: ""
  });
  const [saving, setSaving] = useState(false);
  
  // Delete gallery state
  const [deleteGalleryDialogOpen, setDeleteGalleryDialogOpen] = useState(false);
  const [deletingGallery, setDeletingGallery] = useState(false);
  
  // Edit photo price state
  const [editPriceDialogOpen, setEditPriceDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [newPrice, setNewPrice] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);
  
  // Renewal state
  const [renewing, setRenewing] = useState(false);

  useEffect(() => {
    fetchGallery();
    fetchUploadLimits();
  }, [galleryId]);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/galleries/${galleryId}`, { withCredentials: true });
      setGallery(response.data);
      setAssets(response.data.assets || []);
      // Initialize edit data
      setEditData({
        title: response.data.title || "",
        description: response.data.description || "",
        customer_name: response.data.customer_name || "",
        customer_email: response.data.customer_email || "",
        event_date: response.data.event_date || "",
        is_public: response.data.is_public || false,
        category: response.data.category || "",
        tags: (response.data.tags || []).join(", ")
      });
    } catch (error) {
      toast.error("Gallery not found");
      navigate("/dashboard/galleries");
    } finally {
      setLoading(false);
    }
  };
  
  const handleRenewAccess = async () => {
    setRenewing(true);
    try {
      const response = await axios.post(`${API}/galleries/${galleryId}/renew`, {}, { withCredentials: true });
      toast.success("Gallery access extended by 30 days!");
      // Update local state with new expiry
      setGallery(prev => ({ ...prev, expires_at: response.data.new_expires_at }));
    } catch (error) {
      toast.error("Failed to extend access");
    } finally {
      setRenewing(false);
    }
  };
  
  // Calculate days until expiration
  const getDaysUntilExpiry = () => {
    if (!gallery?.expires_at) return null;
    try {
      const expires = new Date(gallery.expires_at);
      const now = new Date();
      const diffTime = expires - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    } catch {
      return null;
    }
  };

  const fetchUploadLimits = async () => {
    try {
      const response = await axios.get(`${API}/galleries/upload-limits`, { withCredentials: true });
      setUploadLimits(response.data);
    } catch (error) {
      console.error("Failed to fetch upload limits");
    }
  };

  // Calculate totals for selected files
  const calculateTotals = useCallback(() => {
    const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    const totalSizeMB = totalSize / (1024 * 1024);
    return {
      count: selectedFiles.length,
      totalSize,
      totalSizeMB: totalSizeMB.toFixed(2)
    };
  }, [selectedFiles]);

  // Validate against limits
  const validateFiles = useCallback((files) => {
    if (!uploadLimits) return { valid: true, errors: [] };
    
    const limits = uploadLimits.limits;
    const errors = [];
    
    if (limits.max_files_per_upload && files.length > limits.max_files_per_upload) {
      errors.push(`Maximum ${limits.max_files_per_upload} files allowed per upload (${uploadLimits.plan} plan)`);
    }
    
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    const totalSizeMB = totalSize / (1024 * 1024);
    if (limits.max_total_size_mb && totalSizeMB > limits.max_total_size_mb) {
      errors.push(`Total size (${totalSizeMB.toFixed(1)}MB) exceeds ${limits.max_total_size_mb}MB limit (${uploadLimits.plan} plan)`);
    }
    
    for (const file of files) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (limits.max_file_size_mb && fileSizeMB > limits.max_file_size_mb) {
        errors.push(`${file.name} (${fileSizeMB.toFixed(1)}MB) exceeds ${limits.max_file_size_mb}MB per file limit`);
      }
    }
    
    return { valid: errors.length === 0, errors };
  }, [uploadLimits]);

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files).filter(
      f => f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    
    if (files.length > 0) {
      addFiles(files);
    }
  };

  const addFiles = (newFiles) => {
    const existingNames = new Set(selectedFiles.map(f => f.name));
    const uniqueFiles = newFiles.filter(f => !existingNames.has(f.name));
    
    if (uniqueFiles.length < newFiles.length) {
      toast.info(`Skipped ${newFiles.length - uniqueFiles.length} duplicate files`);
    }
    
    const combined = [...selectedFiles, ...uniqueFiles];
    const validation = validateFiles(combined);
    
    if (!validation.valid) {
      validation.errors.forEach(err => toast.error(err));
      return;
    }
    
    setSelectedFiles(combined);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePrices(prev => {
      const newPrices = { ...prev };
      delete newPrices[selectedFiles[index].name];
      return newPrices;
    });
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(
      f => f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    
    if (files.length > 0) {
      addFiles(files);
    }
    
    if (e.target) e.target.value = "";
  };

  const handleBulkUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    setUploadResults(null);
    
    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append("files", file);
    });
    
    let pricesMap = {};
    if (gallery?.is_public) {
      if (pricingMode === "same" && bulkPrice) {
        selectedFiles.forEach(file => {
          pricesMap[file.name] = bulkPrice;
        });
      } else if (pricingMode === "individual") {
        pricesMap = filePrices;
      }
    }
    
    if (Object.keys(pricesMap).length > 0) {
      formData.append("prices", JSON.stringify(pricesMap));
    }
    
    try {
      const response = await axios.post(
        `${API}/galleries/${galleryId}/upload-bulk`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );
      
      setUploadResults(response.data);
      toast.success(`Successfully uploaded ${response.data.uploaded.length} files!`);
      fetchGallery();
      
      setTimeout(() => {
        setSelectedFiles([]);
        setFilePrices({});
        setBulkUploadOpen(false);
        setUploadResults(null);
      }, 2000);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to upload files";
      toast.error(errorMsg);
      setUploadResults({ error: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  // Legacy single file handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (gallery?.is_public) {
        setSelectedFile(file);
        setPriceDialogOpen(true);
      } else {
        uploadFile(file, null);
      }
    }
  };

  const uploadFile = async (file, price) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    if (price) {
      formData.append("price", price);
    }

    try {
      await axios.post(
        `${API}/galleries/${galleryId}/upload`,
        formData,
        { 
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        }
      );
      toast.success("File uploaded!");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      setPriceDialogOpen(false);
      setSelectedFile(null);
      setUploadPrice("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handlePriceSubmit = () => {
    if (selectedFile) {
      uploadFile(selectedFile, uploadPrice ? parseFloat(uploadPrice) : null);
    }
  };

  const handleDeliver = async () => {
    try {
      await axios.post(`${API}/galleries/${galleryId}/deliver`, {}, { withCredentials: true });
      toast.success("Gallery delivered! Customer has been notified.");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to deliver gallery");
    }
  };

  // Selection handlers for bulk delete
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedAssets(new Set());
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const selectAllAssets = () => {
    if (selectedAssets.size === assets.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(assets.map(a => a.asset_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAssets.size === 0) return;
    
    setDeleting(true);
    try {
      const response = await axios.post(
        `${API}/galleries/${galleryId}/delete-assets`,
        { asset_ids: Array.from(selectedAssets) },
        { withCredentials: true }
      );
      
      toast.success(`Successfully deleted ${response.data.deleted_count} photos`);
      setDeleteDialogOpen(false);
      setSelectedAssets(new Set());
      setSelectMode(false);
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete photos");
    } finally {
      setDeleting(false);
    }
  };

  // Edit photo price handlers
  const openEditPriceDialog = (asset, e) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingAsset(asset);
    setNewPrice(asset.price ? asset.price.toString() : "");
    setEditPriceDialogOpen(true);
  };

  const handleSavePrice = async () => {
    if (!editingAsset) return;
    
    setSavingPrice(true);
    try {
      await axios.put(
        `${API}/galleries/${galleryId}/assets/${editingAsset.asset_id}/price`,
        { price: newPrice ? parseFloat(newPrice) : null },
        { withCredentials: true }
      );
      
      toast.success("Price updated!");
      setEditPriceDialogOpen(false);
      setEditingAsset(null);
      setNewPrice("");
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update price");
    } finally {
      setSavingPrice(false);
    }
  };

  // Theater mode handlers
  const openTheaterMode = (index) => {
    if (selectMode) return;
    setTheaterIndex(index);
    setTheaterOpen(true);
  };

  const handleDownload = async (asset) => {
    try {
      const url = `${API}/media/${galleryId}/${asset.filename}?token=${gallery.magic_link_token}`;
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = asset.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Download started!");
    } catch (error) {
      toast.error("Failed to download");
    }
  };

  // Cover photo handlers
  const handleSetCover = async (assetId) => {
    try {
      await axios.put(
        `${API}/galleries/${galleryId}/cover/${assetId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Cover photo updated!");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to set cover photo");
    }
  };

  const handleRemoveCover = async () => {
    try {
      await axios.delete(
        `${API}/galleries/${galleryId}/cover`,
        { withCredentials: true }
      );
      toast.success("Cover photo removed");
      fetchGallery();
    } catch (error) {
      toast.error("Failed to remove cover photo");
    }
  };

  // Edit gallery handlers
  const handleSaveGallery = async () => {
    if (!editData.title) {
      toast.error("Title is required");
      return;
    }

    setSaving(true);
    try {
      // Convert tags string to array
      const dataToSend = {
        ...editData,
        tags: editData.tags.split(",").map(t => t.trim()).filter(Boolean)
      };
      
      await axios.put(
        `${API}/galleries/${galleryId}`,
        dataToSend,
        { withCredentials: true }
      );
      toast.success("Gallery updated!");
      setEditDialogOpen(false);
      fetchGallery();
    } catch (error) {
      toast.error("Failed to update gallery");
    } finally {
      setSaving(false);
    }
  };

  // Delete gallery handler
  const handleDeleteGallery = async () => {
    setDeletingGallery(true);
    try {
      await axios.delete(
        `${API}/galleries/${galleryId}`,
        { withCredentials: true }
      );
      toast.success("Gallery deleted successfully");
      navigate("/dashboard/galleries");
    } catch (error) {
      toast.error("Failed to delete gallery");
    } finally {
      setDeletingGallery(false);
    }
  };

  // Clear bulk upload state helper
  const clearBulkUploadState = () => {
    setSelectedFiles([]);
    setFilePrices({});
    setUploadResults(null);
    setBulkPrice("");
    setPricingMode("same");
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!gallery) {
    return null;
  }

  const totals = calculateTotals();
  const allSelected = assets.length > 0 && selectedAssets.size === assets.length;

  return (
    <div className="space-y-8" data-testid="gallery-detail-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <button
            onClick={() => navigate("/dashboard/galleries")}
            className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Galleries
          </button>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl md:text-4xl tracking-tight">{gallery.title}</h1>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setEditDialogOpen(true)}
              className="text-muted-foreground hover:text-white"
              data-testid="edit-gallery-btn"
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setDeleteGalleryDialogOpen(true)}
              className="text-muted-foreground hover:text-destructive"
              data-testid="delete-gallery-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-muted-foreground">
            {assets.length} photos • {gallery.is_public ? "Public" : "Private"}
            {gallery.is_delivered && " • Delivered"}
          </p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*,video/*"
            className="hidden"
            data-testid="file-input"
          />
          
          {/* Select Mode Toggle */}
          {assets.length > 0 && (
            <Button 
              variant={selectMode ? "secondary" : "outline"}
              onClick={toggleSelectMode}
              data-testid="select-mode-btn"
            >
              {selectMode ? (
                <>
                  <XSquare className="w-4 h-4 mr-2" />
                  Cancel
                </>
              ) : (
                <>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Select
                </>
              )}
            </Button>
          )}
          
          {/* Selection Actions */}
          {selectMode && (
            <>
              <Button 
                variant="outline"
                onClick={selectAllAssets}
                data-testid="select-all-btn"
              >
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
              
              {selectedAssets.size > 0 && (
                <Button 
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedAssets.size})
                </Button>
              )}
            </>
          )}
          
          {/* Upload buttons - hide in select mode */}
          {!selectMode && (
            <>
              <Button 
                variant="outline"
                onClick={() => setBulkUploadOpen(true)}
                data-testid="bulk-upload-btn"
              >
                <FolderUp className="w-4 h-4 mr-2" />
                Bulk Upload
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
              
              {!gallery.is_public && gallery.customer_email && (
                <Button 
                  className="btn-primary rounded-sm"
                  onClick={handleDeliver}
                  data-testid="deliver-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {gallery.is_delivered ? "Resend to Client" : "Deliver to Client"}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Selection Info Bar */}
      {selectMode && (
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5 text-primary" />
            <span className="font-medium">
              {selectedAssets.size} of {assets.length} photos selected
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Click on photos to select them, or use &quot;Select All&quot;
          </p>
        </div>
      )}

      {/* Gallery Info Card */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Type</p>
              <p className="font-medium">{gallery.is_public ? "Public (for sales)" : "Private (client delivery)"}</p>
            </div>
            {gallery.event_date && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Event Date</p>
                <p className="font-medium">{gallery.event_date}</p>
              </div>
            )}
            {gallery.customer_name && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Customer</p>
                <p className="font-medium">{gallery.customer_name}</p>
                {gallery.customer_email && (
                  <p className="text-sm text-muted-foreground">{gallery.customer_email}</p>
                )}
              </div>
            )}
            {gallery.expires_at && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Expires</p>
                <p className="font-medium">{new Date(gallery.expires_at).toLocaleDateString()}</p>
              </div>
            )}
          </div>
          {gallery.description && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-muted-foreground">{gallery.description}</p>
            </div>
          )}
          
          {/* Event Type & Tags */}
          {(gallery.category || (gallery.tags && gallery.tags.length > 0)) && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex flex-wrap items-center gap-2">
                {gallery.category && (
                  <Badge variant="secondary" className="capitalize">
                    <Tag className="w-3 h-3 mr-1" />
                    {gallery.category.replace(/_/g, " ")}
                  </Badge>
                )}
                {gallery.tags && gallery.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Cover Photo Info */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Cover Photo</p>
                <p className="font-medium">
                  {gallery.cover_image ? "Custom cover set" : "Using default (event name & date)"}
                </p>
              </div>
              {gallery.cover_image && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleRemoveCover}
                >
                  Remove Cover
                </Button>
              )}
            </div>
          </div>
          
          {/* Gallery Expiration (for delivered private galleries) */}
          {!gallery.is_public && gallery.is_delivered && gallery.expires_at && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className={`w-5 h-5 ${getDaysUntilExpiry() <= 7 ? 'text-orange-400' : 'text-muted-foreground'}`} />
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Client Access Expires</p>
                    <p className={`font-medium ${getDaysUntilExpiry() <= 7 ? 'text-orange-400' : ''}`}>
                      {new Date(gallery.expires_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                      {getDaysUntilExpiry() !== null && (
                        <span className="text-sm ml-2">
                          ({getDaysUntilExpiry() <= 0 
                            ? 'Expired' 
                            : getDaysUntilExpiry() === 1 
                              ? '1 day left' 
                              : `${getDaysUntilExpiry()} days left`})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <Button 
                  variant={getDaysUntilExpiry() <= 7 ? "default" : "outline"}
                  size="sm"
                  onClick={handleRenewAccess}
                  disabled={renewing}
                  className={getDaysUntilExpiry() <= 7 ? "bg-orange-500 hover:bg-orange-600" : ""}
                  data-testid="renew-access-btn"
                >
                  {renewing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Renewing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      +30 Days
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sell Downloads Pricing - Only for public galleries */}
      {gallery.is_public && (
        <GalleryDownloadPricing 
          galleryId={galleryId} 
          onUpdate={fetchGallery}
        />
      )}

      {/* Assets Grid */}
      {assets.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-lg mb-2">No photos yet</p>
            <p className="text-muted-foreground mb-4">Upload photos to this gallery</p>
            <Button onClick={() => setBulkUploadOpen(true)}>
              <FolderUp className="w-4 h-4 mr-2" />
              Bulk Upload Photos
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {assets.map((asset, index) => {
            const isSelected = selectedAssets.has(asset.asset_id);
            const isCover = gallery.cover_image === asset.asset_id;
            const isVideo = asset.file_type === 'video';
            
            return (
              <motion.div
                key={asset.asset_id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.03 }}
                className={`relative group ${selectMode ? 'cursor-pointer' : 'cursor-pointer'}`}
                onClick={() => selectMode ? toggleAssetSelection(asset.asset_id) : openTheaterMode(index)}
              >
                <div className={`aspect-square bg-muted rounded-sm overflow-hidden transition-all ${
                  isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                } ${isCover ? 'ring-2 ring-yellow-500 ring-offset-2 ring-offset-background' : ''}`}>
                  {isVideo ? (
                    <div className="w-full h-full relative bg-black flex items-center justify-center">
                      <video
                        src={`${API}/media/${galleryId}/${asset.filename}${gallery.is_public ? '' : `?token=${gallery.magic_link_token}`}#t=0.5`}
                        className="w-full h-full object-cover"
                        preload="metadata"
                        muted
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Play className="w-6 h-6 text-white ml-1" fill="white" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <OptimizedImage
                      src={asset.thumbnail_path 
                        ? `${API.replace('/api', '')}${asset.thumbnail_path}${gallery.is_public ? '' : `?token=${gallery.magic_link_token}`}`
                        : `${API}/media/${galleryId}/${asset.filename}${gallery.is_public ? '' : `?token=${gallery.magic_link_token}`}`
                      }
                      fallbackSrc={`${API}/media/${galleryId}/${asset.filename}${gallery.is_public ? '' : `?token=${gallery.magic_link_token}`}`}
                      alt={asset.original_filename || asset.filename}
                      aspectRatio=""
                      className={`w-full h-full transition-all duration-300 ${
                        selectMode ? (isSelected ? 'opacity-100' : 'opacity-70 hover:opacity-100') : 'group-hover:scale-105'
                      }`}
                      maxRetries={2}
                    />
                  )}
                  
                  {/* Cover Photo Badge */}
                  {isCover && (
                    <div className="absolute top-2 right-2 z-10">
                      <span className="px-2 py-1 bg-yellow-500 text-black text-xs font-medium rounded flex items-center gap-1">
                        <Star className="w-3 h-3" fill="currentColor" />
                        Cover
                      </span>
                    </div>
                  )}
                  
                  {/* Selection Checkbox Overlay */}
                  {selectMode && (
                    <div className="absolute top-2 left-2 z-10">
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
                        isSelected 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-black/50 text-white border border-white/30'
                      }`}>
                        {isSelected ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Hover Overlay (only when not in select mode) */}
                  {!selectMode && (
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                      <span className="text-sm">Click to view</span>
                      {asset.price ? (
                        <button
                          className="px-2 py-1 bg-primary/80 rounded-sm text-sm hover:bg-primary transition-colors"
                          onClick={(e) => openEditPriceDialog(asset, e)}
                        >
                          ${asset.price.toFixed(2)} - Edit
                        </button>
                      ) : gallery?.is_public && (
                        <button
                          className="px-2 py-1 bg-muted/80 rounded-sm text-sm hover:bg-muted transition-colors flex items-center gap-1"
                          onClick={(e) => openEditPriceDialog(asset, e)}
                        >
                          <DollarSign className="w-3 h-3" />
                          Set Price
                        </button>
                      )}
                      <div className="flex gap-2 mt-1">
                        {!isCover && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetCover(asset.asset_id);
                            }}
                          >
                            <Star className="w-3 h-3 mr-1" />
                            Cover
                          </Button>
                        )}
                        {gallery?.is_public && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => openEditPriceDialog(asset, e)}
                          >
                            <DollarSign className="w-3 h-3 mr-1" />
                            Price
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Price Badge (visible in select mode) */}
                  {selectMode && asset.price && (
                    <div className="absolute bottom-2 right-2">
                      <span className="px-2 py-1 bg-black/70 rounded-sm text-xs text-white">
                        ${asset.price.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Theater Mode */}
      <TheaterMode
        isOpen={theaterOpen}
        onClose={() => setTheaterOpen(false)}
        assets={assets}
        currentIndex={theaterIndex}
        onIndexChange={setTheaterIndex}
        galleryId={galleryId}
        apiUrl={API}
        magicLinkToken={gallery.magic_link_token}
        isPublic={false}
        onDownload={handleDownload}
      />

      {/* Edit Gallery Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit Gallery</DialogTitle>
            <DialogDescription>
              Update gallery details and contact information
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Gallery Title *</Label>
              <Input
                value={editData.title}
                onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Smith Wedding - June 2024"
                data-testid="edit-title"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Description</Label>
              <Textarea
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description..."
                rows={3}
                data-testid="edit-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Customer Name</Label>
                <Input
                  value={editData.customer_name}
                  onChange={(e) => setEditData(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="John Smith"
                  data-testid="edit-customer-name"
                />
              </div>
              <div>
                <Label className="mb-2 block">Event Date</Label>
                <Input
                  type="date"
                  value={editData.event_date}
                  onChange={(e) => setEditData(prev => ({ ...prev, event_date: e.target.value }))}
                  data-testid="edit-date"
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Customer Email</Label>
              <Input
                type="email"
                value={editData.customer_email}
                onChange={(e) => setEditData(prev => ({ ...prev, customer_email: e.target.value }))}
                placeholder="customer@example.com"
                data-testid="edit-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Event Type</Label>
                <Select
                  value={editData.category}
                  onValueChange={(value) => setEditData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger data-testid="edit-category">
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type.toLowerCase().replace(/\s+/g, "_")}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Tags</Label>
                <Input
                  value={editData.tags}
                  onChange={(e) => setEditData(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="outdoor, golden hour, etc."
                  data-testid="edit-tags"
                />
                <p className="text-xs text-muted-foreground mt-1">Comma separated</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-sm">
              <div>
                <Label>Public Gallery</Label>
                <p className="text-sm text-muted-foreground">
                  Make this gallery publicly viewable for sales
                </p>
              </div>
              <Switch
                checked={editData.is_public}
                onCheckedChange={(checked) => setEditData(prev => ({ ...prev, is_public: checked }))}
                data-testid="edit-public"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveGallery} disabled={saving} data-testid="save-gallery-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Delete Photos
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedAssets.size} photo{selectedAssets.size !== 1 ? 's' : ''}? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">
                <strong>Warning:</strong> Deleted photos will be permanently removed from the gallery 
                and cannot be recovered.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete {selectedAssets.size} Photo{selectedAssets.size !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Gallery Confirmation Dialog */}
      <Dialog open={deleteGalleryDialogOpen} onOpenChange={setDeleteGalleryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Delete Gallery
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this gallery?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">
                <strong>Warning:</strong> This will permanently delete the gallery 
                <strong> &ldquo;{gallery?.title}&rdquo;</strong> and all <strong>{assets.length} photo{assets.length !== 1 ? 's' : ''}</strong> within it.
                This action cannot be undone.
              </p>
            </div>
            
            <div className="text-sm text-muted-foreground">
              <p>The following will be deleted:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Gallery settings and metadata</li>
                <li>All {assets.length} uploaded photos/videos</li>
                <li>Magic link access for this gallery</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteGalleryDialogOpen(false)}
              disabled={deletingGallery}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteGallery}
              disabled={deletingGallery}
            >
              {deletingGallery ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Gallery
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Photo Price Dialog */}
      <Dialog open={editPriceDialogOpen} onOpenChange={setEditPriceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit Photo Price</DialogTitle>
            <DialogDescription>
              Set the price for this photo. Customers can purchase it in public galleries.
            </DialogDescription>
          </DialogHeader>
          
          {editingAsset && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-muted rounded overflow-hidden">
                  <img
                    src={`${API}/media/${galleryId}/${editingAsset.filename}${gallery?.is_public ? '' : `?token=${gallery?.magic_link_token}`}`}
                    alt={editingAsset.filename}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="font-medium truncate max-w-[200px]">{editingAsset.filename}</p>
                  <p className="text-sm text-muted-foreground">
                    Current: {editingAsset.price ? `$${editingAsset.price.toFixed(2)}` : "No price set"}
                  </p>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">New Price (USD)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    placeholder="0.00"
                    className="pl-10"
                    data-testid="edit-photo-price"
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Leave empty to remove the price (preview only)
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPriceDialogOpen(false)} disabled={savingPrice}>
              Cancel
            </Button>
            <Button onClick={handleSavePrice} disabled={savingPrice} data-testid="save-price-btn">
              {savingPrice ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Price
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onOpenChange={(open) => {
        if (!uploading) {
          setBulkUploadOpen(open);
          if (!open) {
            clearBulkUploadState();
          }
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Bulk Upload Photos</DialogTitle>
            <DialogDescription>
              Upload multiple photos at once. Drag and drop or click to select files.
            </DialogDescription>
          </DialogHeader>
          
          {/* Upload Limits Info */}
          {uploadLimits && (
            <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-md text-sm">
              <Info className="w-4 h-4 mt-0.5 text-blue-400" />
              <div>
                <p className="font-medium text-blue-400 capitalize">{uploadLimits.plan} Plan Limits:</p>
                <p className="text-muted-foreground">
                  {uploadLimits.limits.max_file_size_mb 
                    ? `${uploadLimits.limits.max_file_size_mb}MB per file` 
                    : "Unlimited file size"} • 
                  {uploadLimits.limits.max_total_size_mb 
                    ? ` ${uploadLimits.limits.max_total_size_mb}MB total per upload` 
                    : " Unlimited total size"} • 
                  {uploadLimits.limits.max_files_per_upload 
                    ? ` ${uploadLimits.limits.max_files_per_upload} files max` 
                    : " Unlimited files"}
                </p>
              </div>
            </div>
          )}
          
          {/* Pricing Options for Public Galleries */}
          {gallery?.is_public && selectedFiles.length > 0 && (
            <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Photo Pricing
                </Label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPricingMode("same")}
                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                      pricingMode === "same" 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-muted hover:bg-muted/80"
                    }`}
                    disabled={uploading}
                  >
                    Same Price
                  </button>
                  <button
                    type="button"
                    onClick={() => setPricingMode("individual")}
                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                      pricingMode === "individual" 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-muted hover:bg-muted/80"
                    }`}
                    disabled={uploading}
                  >
                    Individual Prices
                  </button>
                </div>
              </div>
              
              {pricingMode === "same" && (
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-muted-foreground whitespace-nowrap">
                    Price for all {selectedFiles.length} photos:
                  </Label>
                  <div className="relative flex-1 max-w-[150px]">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={bulkPrice}
                      onChange={(e) => setBulkPrice(e.target.value)}
                      disabled={uploading}
                      className="pl-9 h-9"
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {bulkPrice ? `= $${(parseFloat(bulkPrice) * selectedFiles.length).toFixed(2)} total` : ""}
                  </span>
                </div>
              )}
              
              {pricingMode === "individual" && (
                <p className="text-xs text-muted-foreground">
                  Set individual prices for each photo in the list below
                </p>
              )}
            </div>
          )}
          
          {/* Drop Zone */}
          <div
            ref={dropZoneRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('bulk-file-input')?.click()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
              ${isDragging 
                ? 'border-primary bg-primary/10' 
                : 'border-border hover:border-primary/50 hover:bg-muted/50'}
              ${uploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input
              id="bulk-file-input"
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleBulkFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <FolderUp className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <p className="font-medium mb-1">
              {isDragging ? "Drop files here" : "Drag & drop photos here"}
            </p>
            <p className="text-sm text-muted-foreground">
              or click to browse • Images and videos only
            </p>
          </div>
          
          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between py-2">
                <p className="text-sm font-medium">
                  {totals.count} files selected • {totals.totalSizeMB} MB total
                </p>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setSelectedFiles([]);
                    setFilePrices({});
                  }}
                  disabled={uploading}
                >
                  Clear All
                </Button>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-2 max-h-[200px] pr-2">
                {selectedFiles.map((file, index) => (
                  <div 
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-3 p-2 bg-muted/30 rounded-md"
                  >
                    <div className="w-10 h-10 bg-muted rounded overflow-hidden flex-shrink-0">
                      {file.type.startsWith('image/') ? (
                        <img 
                          src={URL.createObjectURL(file)} 
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                          <ImageIcon className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                    
                    {gallery?.is_public && pricingMode === "individual" && (
                      <div className="w-24">
                        <div className="relative">
                          <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={filePrices[file.name] || ""}
                            onChange={(e) => setFilePrices(prev => ({
                              ...prev,
                              [file.name]: e.target.value
                            }))}
                            disabled={uploading}
                            className="h-8 text-sm pl-6"
                          />
                        </div>
                      </div>
                    )}
                    
                    {gallery?.is_public && pricingMode === "same" && bulkPrice && (
                      <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded">
                        ${parseFloat(bulkPrice).toFixed(2)}
                      </span>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => removeFile(index)}
                      disabled={uploading}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}
          
          {/* Upload Results */}
          {uploadResults && !uploadResults.error && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-md">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-green-500">Upload Complete!</p>
                <p className="text-sm text-muted-foreground">
                  {uploadResults.uploaded?.length} files uploaded ({uploadResults.total_size_mb} MB)
                </p>
              </div>
            </div>
          )}
          
          {uploadResults?.error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-md">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-500">{uploadResults.error}</p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setBulkUploadOpen(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpload}
              disabled={selectedFiles.length === 0 || uploading}
              className="gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload {selectedFiles.length} {selectedFiles.length === 1 ? 'File' : 'Files'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price Dialog for Single Upload to Public Gallery */}
      <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Set Photo Price</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-muted-foreground">
              This is a public gallery. Set a price for customers to purchase this photo.
            </p>
            <div>
              <Label className="mb-2 block">Price (USD)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={uploadPrice}
                  onChange={(e) => setUploadPrice(e.target.value)}
                  placeholder="0.00"
                  className="pl-10"
                  data-testid="photo-price"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Leave empty for free preview only (no purchase option)
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePriceSubmit} disabled={uploading} data-testid="upload-with-price">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload Photo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryDetailPage;
